import {
  PatientContactDto,
  PatientContactsService,
} from '@services/patientContactService'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface PatientContactPaginatedDto {
  datas: PatientContactDto[]
  page: number
  limit: number
  total: number
  total_pages: number
}

export interface PatientContactState {
  patientContacts: PatientContactPaginatedDto
  loading: boolean
}

const initialState: PatientContactState = {
  patientContacts: {
    datas: [],
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
  },
  loading: false,
}

export const doFetchPatientContacts = createAsyncThunk(
  'patientContact/fetchPatientContacts',
  async (patientId: number) => {
    const response = await PatientContactsService.getByPatientId(patientId)
    return response
  },
)

export const doCreatePatientContact = createAsyncThunk(
  'patientContact/createPatientContact',
  async (data: Omit<PatientContactDto, 'id'>) => {
    const response = await PatientContactsService.create(data)
    return response
  },
)

export const doUpdatePatientContact = createAsyncThunk(
  'patientContact/updatePatientContact',
  async ({
    id,
    data,
  }: {
    id: number
    data: Partial<Omit<PatientContactDto, 'id'>>
  }) => {
    const response = await PatientContactsService.update(id, data)
    return response
  },
)

export const doDeletePatientContact = createAsyncThunk(
  'patientContact/deletePatientContact',
  async (id: number) => {
    const response = await PatientContactsService.delete(id)
    return response
  },
)

const patientContactSlice = createSlice({
  name: 'patientContact',
  initialState,
  reducers: {
    clearPatientContactsData: (state) => {
      state.patientContacts = {
        datas: [],
        page: 1,
        limit: 20,
        total: 0,
        total_pages: 0,
      }
    },
    setPatientContacts: (
      state,
      action: PayloadAction<PatientContactPaginatedDto>,
    ) => {
      state.patientContacts = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(doFetchPatientContacts.pending, (state) => {
      state.loading = true
    })
    builder.addCase(doFetchPatientContacts.fulfilled, (state, action) => {
      state.loading = false
      state.patientContacts.datas = action.payload
      state.patientContacts.total = action.payload.length
      state.patientContacts.total_pages = Math.ceil(
        action.payload.length / state.patientContacts.limit,
      )
    })
    builder.addCase(doCreatePatientContact.pending, (state) => {
      state.loading = true
    })
    builder.addCase(doCreatePatientContact.fulfilled, (state, action) => {
      state.loading = false
      state.patientContacts.datas.push(action.payload)
      state.patientContacts.total++
      state.patientContacts.total_pages = Math.ceil(
        state.patientContacts.total / state.patientContacts.limit,
      )
    })
    builder.addCase(doUpdatePatientContact.fulfilled, (state, action) => {
      state.loading = false
      const index = state.patientContacts.datas.findIndex(
        (patientContact) => patientContact.id === action.payload.id,
      )
      if (index !== -1) {
        state.patientContacts.datas[index] = action.payload
      }
    })
    builder.addCase(doDeletePatientContact.fulfilled, (state, action) => {
      state.loading = false
      state.patientContacts.datas = state.patientContacts.datas.filter(
        (patientContact) => patientContact.id !== action.payload.id,
      )
      state.patientContacts.total--
      state.patientContacts.total_pages = Math.ceil(
        state.patientContacts.total / state.patientContacts.limit,
      )
    })
  },
})

export const { clearPatientContactsData, setPatientContacts, setLoading } =
  patientContactSlice.actions
export default patientContactSlice.reducer
